import { useCallback } from 'react'

import { Box, styled } from '@mui/material'
import { PickersDayProps } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'

import { format, getDate, setDate } from 'date-fns'

import type { ValidColor } from '@/local/components'
import { useDateFnsLocale } from '../../../hooks/useDateFnsLocale'
import { Dot } from '../../Dot'

import type {
  CalendarClickType,
  MonthlyValidSelection,
  MonthCalendarPublicProps as Props,
  SelectionAsRecord,
} from '../../typings'

type CustomDayProps = PickersDayProps<Date> & {
  highlightedDays?: SelectionAsRecord
  dotColor: ValidColor
}

const epoch = new Date(1970, 0, 1)

export function Calendar(props: Props) {
  const { selection, onChange, highlightedDays, dotColor = '$pigmentGreen' } = props

  const locale = useDateFnsLocale()

  const selectedDate = selection ? setDate(epoch, selection) : undefined

  const handleOnChange = useCallback(
    (date: Date, type: CalendarClickType = 'click') => {
      const dayDate = getDate(date) as MonthlyValidSelection
      onChange(dayDate, selection, type)
    },
    [onChange, selection],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <CustomDateCalendar
        value={selectedDate}
        onChange={value => handleOnChange(value)}
        views={['day']}
        slots={{
          calendarHeader: () => <div />,
          day: CustomDay,
        }}
        slotProps={{
          day: ({ day }) =>
            ({
              highlightedDays,
              dotColor,
              'data-testid': format(day, 'dd'),
            }) as any,
        }}
      />
    </LocalizationProvider>
  )
}

const CustomDateCalendar = styled(DateCalendar)(() => ({
  '& .MuiDayCalendar-header': {
    display: 'none',
  },
  '&.MuiDateCalendar-root': {
    width: 'auto',
    height: 'auto',
    marginTop: '24px',
    marginBottom: '32px',
  },
  '& .MuiPickersSlideTransition-root.MuiDayCalendar-slideTransition': {
    minHeight: '200px',
  },
}))

const CustomDay = (props: CustomDayProps) => {
  const { day, selected, highlightedDays, dotColor, ...other } = props

  const formattedDate = format(day, 'yyyyMMdd')
  const isHighlighted = !!highlightedDays && !!highlightedDays[formattedDate]

  return (
    <Box position="relative" display="flex">
      <PickersDay {...other} day={day} selected={selected} />
      {isHighlighted && <Dot background={dotColor} />}
    </Box>
  )
}
